/**
 * Server-authoritative transaction Trends data hook for the `/transactions` page.
 *
 * The request mirrors the canonical FilterState, uses the existing 60-second
 * insights cache, and never falls back to the paginated transaction snapshot.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { stableStringify, useInsightsCache } from '@/hooks/useInsightsCache';
import { getDateRangeFromFilter, type FilterState } from '@/lib/filters';
import type {
  TransactionTrendsGranularity,
  TransactionTrendsPayload,
} from '@/lib/resolverContracts';
import api, { handleApiError, type ApiResponse } from '@/services/api';

const TRANSACTION_TRENDS_ENDPOINT = '/insights/transaction-trends';
const INSIGHTS_CACHE_TTL_MS = 60_000;
const DAY_MS = 24 * 60 * 60 * 1000;

const PERIOD_WINDOW_DAYS: Readonly<Record<FilterState['period'], number>> = {
  '1d': 1,
  '2d': 2,
'3d': 3,
  '5d': 5,
  '1w': 7,
  '2w': 14,
  '1m': 30,
  '7d': 7,
  '30d': 30,
  '3m': 90,
  '6m': 180,
  '1y': 365,
  all: 730,
  custom: 30,
};

type SerializableFilterState = Omit<FilterState, 'dateFrom' | 'dateTo'> & {
  dateFrom?: string;
  dateTo?: string;
  timezone?: string;
};

export type TransactionTrendsRequestBody = SerializableFilterState & {
  kind: 'transaction-trends';
  granularity: TransactionTrendsGranularity;
};

export interface UseTransactionTrendsResult {
  data: TransactionTrendsPayload | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function serialiseDate(value?: Date | string): string | undefined {
  return typeof value === 'string' ? value : value?.toISOString();
}

/** Preserve every active FilterState field while converting Date values to JSON. */
export function serialiseTransactionTrendsFilterState(
  filterState: FilterState,
  timezone?: string,
): SerializableFilterState {
  const { dateFrom, dateTo, ...rest } = filterState;

  return {
    ...rest,
    ...(dateFrom ? { dateFrom: serialiseDate(dateFrom) } : {}),
    ...(dateTo ? { dateTo: serialiseDate(dateTo) } : {}),
    ...(timezone ? { timezone } : {}),
  };
}

export function buildTransactionTrendsRequestBody(
  filterState: FilterState,
  granularity: TransactionTrendsGranularity,
  timezone?: string,
): TransactionTrendsRequestBody {
  return {
    kind: 'transaction-trends',
    ...serialiseTransactionTrendsFilterState(filterState, timezone),
    granularity,
  };
}

/** Exact content-addressed key required by the FE-1 contract. */
export function buildTransactionTrendsCacheKey(
  filterState: FilterState,
  granularity: TransactionTrendsGranularity,
  timezone?: string,
): string {
  const serialisedFilterState = serialiseTransactionTrendsFilterState(filterState, timezone);
  return stableStringify({ filterState: serialisedFilterState, granularity });
}

/** Mirror the backend's existing auto-granularity thresholds. */
export function resolveTransactionTrendsGranularity(
  windowDays: number,
): Exclude<TransactionTrendsGranularity, 'auto'> {
  if (windowDays <= 45) return 'day';
  if (windowDays <= 180) return 'week';
  return 'month';
}

/** Resolve the floating-bar window without creating a second date-window owner. */
export function getTransactionTrendsWindowDays(
  filterState: FilterState,
  timezone?: string,
): number {
  const range = getDateRangeFromFilter(
    filterState.period,
    filterState.dateFrom,
    filterState.dateTo,
    timezone,
  );

  if (filterState.period === 'custom' && range.dateFrom && range.dateTo) {
    const from = Date.parse(range.dateFrom);
    const to = Date.parse(range.dateTo);
    if (Number.isFinite(from) && Number.isFinite(to) && to >= from) {
      return Math.max(1, Math.floor((to - from) / DAY_MS) + 1);
    }
  }

  return PERIOD_WINDOW_DAYS[filterState.period];
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'CanceledError' || error.name === 'AbortError')
  );
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const response = (error as {
      response?: { data?: { error?: { message?: unknown }; message?: unknown } };
    }).response;
    const nestedMessage = response?.data?.error?.message;
    if (typeof nestedMessage === 'string' && nestedMessage.length > 0) return nestedMessage;
    const topLevelMessage = response?.data?.message;
    if (typeof topLevelMessage === 'string' && topLevelMessage.length > 0) return topLevelMessage;
  }

  return handleApiError(error) || "We couldn't load Trends insights.";
}

interface FetchState {
  cacheKey: string;
  data: TransactionTrendsPayload | null;
  loading: boolean;
  error: string | null;
}

export function useTransactionTrends(
  filterState: FilterState,
  granularity: TransactionTrendsGranularity,
  timezone?: string,
): UseTransactionTrendsResult {
  const cacheKey = buildTransactionTrendsCacheKey(filterState, granularity, timezone);
  const requestBody = useMemo(
    () => buildTransactionTrendsRequestBody(filterState, granularity, timezone),
    // `cacheKey` is the stableStringify content address for these inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey],
  );
  const insightsCache = useInsightsCache<TransactionTrendsPayload>({
    ttlMs: INSIGHTS_CACHE_TTL_MS,
  });

  const [state, setState] = useState<FetchState>({
    cacheKey: '',
    data: null,
    loading: true,
    error: null,
  });
  const [refetchTick, setRefetchTick] = useState(0);
  const forceRefetchRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const activeControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    const activeController = activeControllerRef.current;
    if (activeController && !activeController.signal.aborted) return;
    forceRefetchRef.current = true;
    setRefetchTick((tick) => tick + 1);
  }, []);

  useEffect(() => {
    const forceRefetch = forceRefetchRef.current;
    forceRefetchRef.current = false;

    if (!forceRefetch) {
      const cached = insightsCache.get(cacheKey);
      if (cached && !cached.isStale) {
        setState({ cacheKey, data: cached.data, loading: false, error: null });
        return;
      }
    }

    const controller = new AbortController();
    const requestSequence = ++requestSequenceRef.current;
    activeControllerRef.current = controller;
    insightsCache.beginFetch(cacheKey, controller);
    setState({ cacheKey, data: null, loading: true, error: null });

    api
      .post<ApiResponse<TransactionTrendsPayload>>(
        TRANSACTION_TRENDS_ENDPOINT,
        requestBody,
        { signal: controller.signal },
      )
      .then((response) => {
        if (controller.signal.aborted || requestSequence !== requestSequenceRef.current) return;
        const payload = response.data?.data;
        if (!payload) throw new Error("We couldn't load Trends insights.");
        insightsCache.set(cacheKey, payload);
        setState({ cacheKey, data: payload, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          requestSequence !== requestSequenceRef.current ||
          isAbortError(error)
        ) {
          return;
        }
        setState({ cacheKey, data: null, loading: false, error: getErrorMessage(error) });
      })
      .finally(() => {
        insightsCache.endFetch(cacheKey);
        if (requestSequence === requestSequenceRef.current) {
          activeControllerRef.current = null;
        }
      });

    return () => {
      controller.abort('cache:filter-changed');
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
    };
    // The cache API methods are stable callbacks; requestBody is content-stable via cacheKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, refetchTick]);

  if (state.cacheKey !== cacheKey) {
    return { data: null, loading: true, error: null, refetch };
  }

  return { data: state.data, loading: state.loading, error: state.error, refetch };
}

export default useTransactionTrends;
